// apps/cli/src/commands/bootstrap/ui.ts
//
// CLI-rewrite: DEPRECATED. This command is inert and kept only as a signpost.
//
// It used to be the install's step 4b: wait for the installer's foundry-create
// phase, grant the founder Foundry data-plane access, deploy a dedicated intake
// agent (`ezra-intake`), write apps/web/.env.local, install the web app's deps,
// and serve a local Next.js chat on :3000 so the founder could be interviewed
// while the platform installed. Optionally a voice relay on :8790 too.
//
// Three things ended it:
//
//   1. The interview had already collapsed to one confirmation (the founder's
//      email) and one question (their Microsoft startup advisor). A whole product
//      vertical — an agent, a persona, a model cascade, a local web server, a
//      voice stack — was standing behind two questions.
//   2. It could not run at all for a founder. The local chat is part of the
//      platform's web app, which is not published, so `assertPlatformCheckout`
//      refused the whole command from the repository a founder actually clones —
//      taking the Foundry role grant and the agent deploy down with it.
//   3. The founder-facing half is better served by the hosted Ezra on m8t.run,
//      which is bigger, already running, and costs an install nothing.
//
// The replacements: `m8t bootstrap profile` (the two questions, deterministically,
// plus the chat hand-off) and `m8t open` (their own Ezra, once it exists).
//
// NOTHING WAS DELETED. `lib/bootstrap-ui.ts`, `lib/intake-agent.ts`,
// `lib/intake-model.ts`, `lib/subscription-inventory.ts` and
// `personas/ezra-intake/` are all still here, unreferenced from this path, so the
// CLI rewrite can see exactly what it is replacing. `--stop` still works, because
// a founder who ran the old command may still have a server to shut down.

import { Command, Option } from "clipanion";
import { M8tCommand } from "../../lib/m8t-command.js";
import { colors } from "../../lib/output.js";
import { stopOnboardingUi } from "../../lib/bootstrap-ui.js";

/**
 * One line, both replacements named. It is not an error — the install is
 * unaffected and always was — so this exits 0.
 */
export function renderDeprecationNotice(): string {
  return (
    `${colors.error("⚠")} 'm8t bootstrap ui' is deprecated and does nothing — the local onboarding chat is gone for good.\n` +
    `  Your details + chat while you wait: ${colors.field("m8t bootstrap profile")} · ` +
    `Your own Ezra, once installed: ${colors.field("m8t open")}\n`
  );
}

export class BootstrapUiCommand extends M8tCommand {
  static paths = [["bootstrap", "ui"]];
  static usage = Command.Usage({
    description: "DEPRECATED — does nothing. Use `m8t bootstrap profile` instead.",
    details: [
      "The local onboarding chat has been retired. Your details are collected by",
      "`m8t bootstrap profile`, which also opens the hosted Ezra to talk to while the",
      "install runs; `m8t open` reaches your own Ezra once the install finishes.",
      "",
      "This command deploys nothing, serves nothing, and changes nothing. Every flag",
      "below is accepted and ignored, so an older runbook or script still exits cleanly.",
      "`--stop` is the one exception: it still shuts down a chat server left running by",
      "an earlier version of this command.",
    ].join("\n"),
    examples: [
      ["Collect your details and open Ezra instead", "$0 bootstrap profile"],
      ["Stop a chat UI an older CLI left running", "$0 bootstrap ui --stop"],
    ],
  });

  // Accepted and ignored, deliberately: removing them would turn an old script's
  // harmless no-op into an "unknown option" failure mid-install. CLI-rewrite: drop
  // the whole surface when the rewrite lands.
  repoRoot = Option.String("--repo-root", { description: "Ignored (deprecated)." });
  port = Option.String("--port", "3000", { description: "Ignored (deprecated)." });
  endpoint = Option.String("--endpoint", { description: "Ignored (deprecated)." });
  prepOnly = Option.Boolean("--prep-only", false, { description: "Ignored (deprecated)." });
  skipInstall = Option.Boolean("--skip-install", false, { description: "Ignored (deprecated)." });
  foreground = Option.Boolean("--foreground", false, { description: "Ignored (deprecated)." });
  voice = Option.Boolean("--voice", false, { description: "Ignored (deprecated)." });
  stop = Option.Boolean("--stop", false, {
    description: "Shut down a local chat UI left running by an earlier version of this command.",
  });

  // Not `async`: there is nothing left to await. Everything this command used to
  // wait on — Foundry, the role grant, the agent deploy, pnpm — is gone.
  executeCommand(): Promise<number> {
    // The one branch that still does work. It never needed bootstrap state or
    // Foundry, and a founder who ran the old command on this machine may still have
    // a detached server and a pid file to clean up.
    if (this.stop === true) {
      const stopped = stopOnboardingUi();
      this.context.stdout.write(
        stopped
          ? `${colors.success("✓")} stopped the onboarding chat UI.\n`
          : `  ${colors.dim("nothing to stop (no onboarding-ui.pid found).")}\n`,
      );
      return Promise.resolve(0);
    }

    this.context.stdout.write(renderDeprecationNotice());
    return Promise.resolve(0);
  }
}
